import React, { useCallback, useState } from 'react';
import { ticketColumnsV2 } from '../../../tableColumns/hardwareColumns';
import { history } from '../../../utils';
import { IHardware } from '../../../ts/interface/hardware.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import CardView from '../../../components/Redesing/card-view';
import { ITicket } from '../../../ts/interface/problem.interface';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import { MovaticFieldGroupSelectFilter } from '../../../components/Redesing/movatic-field-group';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import useUrlState from '../../../hooks/use-url-state';

const STATUS = [
  { label: 'Canceled', value: 'Canceled' },
  { label: 'Closed', value: 'Closed' },
  { label: 'Assigned', value: 'Assigned' },
  { label: 'Open', value: 'Open' },
];

const HardwareAssignment = ({
  hardware,
  isLoading,
}: {
  hardware: IHardware;
  isLoading: boolean;
}) => {
  const { type, hardwareId } = useParams<{ type: string; hardwareId: string }>();
  const [exportData, setExportData] = useState(false);

  const [filters, setFilters] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    pageNumber: 0,
    search: '',
    selectedStatus: [{ label: 'Open', value: 'Open' }] as Option[],
    status: 'Open',
    tags: '',
    sortConfig: {
      key: 'due_on',
      direction: 'asc',
    } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([
    {
      label: 'Status',
      field: 'status',
      value: 'Open',
      displayValue: 'Open',
      disabled: false,
    },
  ]);

  const getStatus = (tickets: ITicket[]) => {
    const today = new Date().setHours(0, 0, 0, 0);

    return (
      tickets
        ?.map((ticket) => {
          const dueDate = new Date(ticket.due_on).setHours(0, 0, 0, 0);
          const pastDue = dueDate < today ? ' - Past Due' : '';

          const status = ticket.canceled_on
            ? 'Canceled'
            : ticket.completed_on
            ? 'Closed'
            : ticket.assignee
            ? `Assigned${pastDue}`
            : `Open${pastDue}`;

          return { ...ticket, status };
        })
        .filter((ticket) => {
          const selectedStatus = filters.status
            .split(',')
            .map((s) => s.trim().toLowerCase());
          return selectedStatus.includes(ticket.status.toLowerCase());
        }) || []
    );
  };

  const handleChipsChange = useCallback(
    (
      values: string[],
      list: { label: string; value: string }[],
      metadata: { label: string; value: string }
    ): void => {
      setFilters((prevState) => ({
        ...prevState,
        pageNumber: 0,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        [metadata.value]: values.join(','),
      }));

      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setFilters]
  );

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });

      setFilters((prevState) => ({
        ...prevState,
        pageNumber: 0,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        status: 'Open',
      }));
    },
    [setFilters]
  );

  return (
    <>
      <CardView
        headerActions={
          <IconButtonMenu
            buttons={[
              {
                label: 'Export',
                onClick: () => setExportData(true),
                startIcon: <FileDownloadIcon />,
              },
              {
                startIcon: <AddIcon />,
                variant: 'contained',
                label: 'Add',
                onClick: () => history.push(`/maintenance/add/unit/${hardwareId}`),
              },
            ]}
          />
        }
        isLoading={isLoading}
        content={
          <>
            <PaginationTable
              dataId="hardware-tickets"
              customKey={'id'}
              onRowCLick={(id: string) => history.push(`/maintenance/${id}`)}
              exportData={exportData}
              chips={chips}
              handleChipDelete={handleChipDelete}
              csvFileName={'Hardware_Tickets.csv'}
              onExport={() => setExportData(false)}
              onApplyFilter={() => {
                if (
                  JSON.stringify(filters.selectedStatus) !==
                  JSON.stringify(filters.status)
                ) {
                  handleChipsChange(
                    [
                      ...filters.selectedStatus
                        .filter((option) =>
                          STATUS.some((item) => item.label === option.label)
                        )
                        .map((item) => item.value.toString()),
                    ],
                    STATUS,
                    { label: 'Status', value: 'status' }
                  );
                }
              }}
              spaceFilters={
                <>
                  <MovaticFieldGroupSelectFilter
                    dataId="hardware-tickets-status"
                    label={'Status'}
                    value={filters.selectedStatus}
                    onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                      setFilters({
                        ...filters,
                        selectedStatus: newValue,
                      });
                    }}
                    options={STATUS}
                  />
                </>
              }
              sortDirection={filters.sortConfig.direction}
              sortBy={filters.sortConfig.key}
              onSortChange={(sort) =>
                setFilters((prevState) => ({
                  ...prevState,
                  sortConfig: sort as ISortConfig,
                }))
              }
              handleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFilters((prevState) => ({
                  ...prevState,
                  search: e.target.value,
                }))
              }
              searchText={filters.search}
              columns={ticketColumnsV2 as IColumn[]}
              items={getStatus(hardware?.tickets)}
              onPageChange={(page, pageN) => {
                setFilters((prevState) => ({
                  ...prevState,
                  pageNumber: pageN,
                }));
              }}
              onRowsPerPageChange={(row) => {
                const pageSize = Number(row.target.value);
                setFilters((prevState) => ({
                  ...prevState,
                  pageNumber: 0,
                  sizePerPage: pageSize,
                }));
              }}
              page={filters.pageNumber}
              rowsPerPage={filters.sizePerPage}
              searchPlaceholder={'Search Maintenance Tickets'}
              noDataText={`No maintenance tickets for this ${type}`}
            />
          </>
        }
        title={'Maintenance Tickets'}
      />
    </>
  );
};

export default HardwareAssignment;
